import React, {
  useEffect,
  useState,
  useTransition,
  useMemo,
  memo,
} from 'react';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Box,
  Button,
} from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import { validateUserAddress } from './utils';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { environment } from 'apps/table-tuck-web/src/environments/environment';
import {
  LoadDeliveryFees,
  setDeliveryStore,
  setUser,
  userAddresses,
} from '../../../../../../libs/redux-slices/src';
import { useDispatch } from 'react-redux';
import Snipper from 'libs/components/src/lib/snipper/Snipper';
import AddressHandlerDialog from '../Address/AddressHandler';
import { textAlign } from '@mui/system';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { auth, firestore } from '@tabletuck/firebase-services';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeAPICall } from './utils';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import { calculateDistance } from '../../services/locationService';

// Static styles - created once, reused on every render
const STATIC_STYLES = {
  dialogContent: {
    padding: '24px 32px 32px',
    marginTop: '16px',
  },
  closeButtonBox: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    zIndex: 1,
  },
  storefrontIconSmall: {
    color: 'white',
    fontSize: '28px',
  },
  titleText: {
    fontWeight: 700,
    fontFamily: 'Jost, sans-serif',
    fontSize: '24px',
    color: '#172B4D',
    marginBottom: '8px',
  },
  subtitleText: {
    fontFamily: 'Jost, sans-serif',
    fontSize: '14px',
    color: '#666',
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    py: 2,
  },
  addressContentBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
  },
  addressTextBox: {
    flex: 1,
  },
  displayNameText: {
    fontFamily: 'Jost, sans-serif',
    fontSize: '14px',
    color: '#666',
    lineHeight: 1.4,
  },
  temporaryText: {
    fontFamily: 'Jost, sans-serif',
    fontSize: '12px',
    color: '#999',
    fontStyle: 'italic',
    marginTop: '4px',
  },
  addButtonBox: {
    mt: 3,
  },
};

function DeliveryDialog({
  token,
  setErr,
  open,
  handleClose,
  items,
  handleDefault,
  handleCloseCartDialoge,
  setAddressStore,
  handleFee,
  setApiError,
  onAddressChange, // Callback to handle address changes from parent
  currentStore, // Store data for 5-mile delivery radius check
}) {
  const [t, i18n] = useTranslation();
  const [loading, setLoading] = useState(null);
  const [isPending, startTransition] = useTransition();
  const dispatch = useDispatch();
  const isFatty =
    environment?.restaurantId === 'fattyPatty' ||
    environment?.restaurantId === 'fattyPattyDev';
  const [openAdd, setOpenAdd] = React.useState(false);
  const [distanceError, setDistanceError] = React.useState('');

  const visibleItems = useMemo(
    () => (items || []).filter((item) => item?.isDisplayed !== false),
    [items]
  );

  // Auto-open add form when dialog opens and there are no saved addresses yet
  // addresses are passed in via `items` prop; no need to select from redux here
  const user = useSelector((state) => state.authReducer.userData);

  // Use unified delivery address hook to check if address was set from branch menu
  const { isAddressFromBranchMenu, setDeliveryAddressById } =
    useDeliveryAddress();

  useEffect(() => {
    if (open && visibleItems.length === 0 && !distanceError) {
      setOpenAdd(true);
    }
  }, [open, visibleItems.length, distanceError]);

  // Memoize all dynamic styles that depend on environment.mainColor
  const dialogRootSx = useMemo(
    () => ({
      zIndex: (theme) => theme.zIndex.modal + 200,
    }),
    []
  );

  const dialogPaperSx = useMemo(
    () => ({
      borderRadius: '24px',
      padding: 0,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${environment.mainColor}20`,
      zIndex: (theme) => theme.zIndex.modal + 200,
    }),
    []
  );

  const backdropSx = useMemo(
    () => ({
      zIndex: (theme) => theme.zIndex.modal + 199,
    }),
    []
  );

  const closeIconSx = useMemo(
    () => ({
      color: '#666',
      cursor: 'pointer',
      fontSize: '28px',
      padding: '8px',
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      '&:hover': {
        backgroundColor: '#f5f5f5',
        color: environment.mainColor,
      },
    }),
    []
  );

  const dialogTitleSx = useMemo(
    () => ({
      textAlign: 'center',
      background: `linear-gradient(135deg, ${environment.mainColor}08 0%, ${environment.mainColor}15 100%)`,
      borderRadius: '24px 24px 0 0',
      padding: '32px 24px 24px',
      position: 'relative',
    }),
    []
  );

  const iconContainerSx = useMemo(
    () => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      backgroundColor: environment.mainColor,
      marginBottom: '16px',
    }),
    []
  );

  const loadingTextSx = useMemo(
    () => ({
      fontFamily: 'Jost, sans-serif',
      fontSize: '16px',
      fontWeight: 500,
      color: environment.mainColor,
    }),
    []
  );

  const addressIconBoxSx = useMemo(
    () => ({
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: `${environment.mainColor}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }),
    []
  );

  const addressIconSx = useMemo(
    () => ({
      color: environment.mainColor,
      fontSize: '18px',
    }),
    []
  );

  const nickNameTextSx = useMemo(
    () => ({
      fontFamily: 'Jost, sans-serif',
      fontSize: '16px',
      color: environment.mainColor,
      fontWeight: 600,
      marginBottom: '4px',
    }),
    []
  );

  const checkIconSx = useMemo(
    () => ({
      color: environment.mainColor,
      fontSize: '24px',
    }),
    []
  );

  const addButtonSx = useMemo(
    () => ({
      borderColor: environment.mainColor,
      color: environment.mainColor,
      borderRadius: '16px',
      py: 2,
      fontFamily: 'Jost, sans-serif',
      textTransform: 'none',
      fontSize: '16px',
      fontWeight: 600,
      border: `2px solid ${environment.mainColor}`,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      '&:hover': {
        borderColor: environment.mainColor,
        backgroundColor: environment.mainColor,
        color: 'white',
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 16px ${environment.mainColor}30`,
      },
    }),
    []
  );

  // Function to get address item box sx based on selection state
  const getAddressBoxSx = useMemo(
    () => (isSelected) => ({
      padding: '20px',
      marginBottom: '16px',
      border: isSelected
        ? `2px solid ${environment.mainColor}`
        : '1px solid #E6E6E6',
      borderRadius: '16px',
      cursor: 'pointer',
      position: 'relative',
      background: isSelected ? `${environment.mainColor}05` : 'white',
      transition: 'all 0.2s ease-in-out',
      willChange: 'transform, box-shadow',
      boxShadow: isSelected ? `0 2px 12px ${environment.mainColor}25` : 'none',
      '&:hover': {
        borderColor: environment.mainColor,
        backgroundColor: `${environment.mainColor}08`,
        boxShadow: `0 4px 16px ${environment.mainColor}20`,
      },
    }),
    []
  );

  const validateUserAddress = async (token, address, addressId) => {
    const { city, displayName, state, route, streetNumber, lat, lng, zipCode } =
      address;
    try {
      const tokenId = await auth?.currentUser
        .getIdToken()
        .then((token) => token);
      const headers = {
        'Content-Type': 'application/json',
        Authorization: tokenId,
      };

      const { data } = await axios.post(
        `${environment?.apiUrl}/getStores?restaurantId=${environment.restaurantId}`,
        {
          uid: token,
          city,
          state,
          displayName,
          route,
          streetNumber,
          lat,
          lng,
          zipCode,
        },
        { headers }
      );
      if (!data.delivery.length) {
        throw new Error(t('basket.addressOutService'));
      } else {
        await firestore.doc(`users/${token}`).set(
          {
            defaultDeliveryAddress: addressId,
          },
          {
            merge: true,
          }
        );
        await firestore
          .doc(`users/${token}`)
          .collection('addresses')
          .doc(addressId)
          .set(
            {
              delivery_store_id: data.delivery[0].id,
            },
            {
              merge: true,
            }
          );

        await firestore
          .collection('users')
          .doc(user?.uid)
          .collection('addresses')
          .get()
          .then((docs) => {
            let adds = [];
            docs.forEach((doc) => {
              adds.push(doc.data());
            });
            dispatch(userAddresses(adds));
          });
        const updatedUser = { ...user, defaultDeliveryAddress: addressId };
        dispatch(setUser({ userData: updatedUser }));

        const doc = await firestore
          .collection('users')
          .doc(token)
          .collection('addresses')
          .doc(addressId)
          .get();
        if (doc?.exists) {
          return {
            displayName: doc.data().displayName,
            storeId: data.delivery[0].id,
          };
        }
      }
    } catch (e) {
      setApiError(e.message);
      //call error (e)
      return e.message;
    }
  };
  const handleClickOpen = () => {
    setDistanceError('');
    startTransition(() => {
      setOpenAdd(true);
    });
  };
  const handleCloseAdd = () => {
    setOpenAdd(false);
  };
  // Handle when a new address is added
  const handleAddressAdded = (newAddress) => {
    if (isFatty) {
      // Check 5-mile delivery radius restriction
      if (newAddress.lat && newAddress.lng && currentStore) {
        const storeCoords =
          currentStore._geoloc ||
          currentStore.coordinates ||
          currentStore.address;
        if (storeCoords?.lat && storeCoords?.lng) {
          const distanceKm = calculateDistance(storeCoords, newAddress);
          if (distanceKm !== null && distanceKm * 0.621371 > 5) {
            handleCloseAdd();
            setDistanceError(
              'Delivery is not available for this address. It is more than 5 miles from the store.'
            );
            return false; // tells AddressHandler to keep the dialog open
          }
        }
      }
      setDistanceError('');

      // Close dialog immediately for instant feedback
      handleCloseAdd();

      // Defer all state updates to not block UI
      startTransition(() => {
        // Use the unified hook to set delivery address with source
        // This respects the priority system (branchMenu > deliveryDialog)
        setDeliveryAddressById(
          newAddress.id,
          newAddress.delivery_store_id || '',
          'deliveryDialog'
        );

        handleFee && handleFee(0);
        dispatch(LoadDeliveryFees(0));

        handleDefault(
          newAddress?.displayName || newAddress?.nickName || newAddress?.id
        );
        setErr && setErr('');
        setApiError && setApiError('');

        // Call parent callback
        if (onAddressChange && typeof onAddressChange === 'function') {
          onAddressChange(newAddress.id);
        }
      });

      // Fire and forget Firebase update (only if NOT from branchMenu)
      if (!isAddressFromBranchMenu) {
        setTimeout(() => {
          firestore
            .doc(`users/${token}`)
            .set({ defaultDeliveryAddress: newAddress.id }, { merge: true })
            .catch((error) => {
              // Error handled silently
            });
        }, 0);
      }

      return;
    }

    // For non-FattyPatty
    (async () => {
      try {
        const res = await validateUserAddress(token, newAddress, newAddress.id);
        const fee = await makeAPICall(
          user?.defaultStore,
          newAddress?.id,
          user?.uid
        );

        startTransition(() => {
          handleFee(fee);
          dispatch(LoadDeliveryFees(fee));

          const { storeId } = res || {};

          if (!res.displayName) {
            handleDefault(res);
            setErr('');
          } else {
            handleDefault(res.displayName);
            dispatch(
              setDeliveryStore({
                address: newAddress.id,
                store: storeId,
                source: 'deliveryDialog', // Lower priority than branchMenu
              })
            );
            setAddressStore(storeId);
          }

          // Call parent callback
          if (onAddressChange && typeof onAddressChange === 'function') {
            onAddressChange(newAddress.id);
          }
        });

        handleCloseAdd();
      } catch (error) {
        setApiError && setApiError(error.message || 'Failed to select address');
      }
    })();
  };
  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth="sm"
      fullWidth
      sx={dialogRootSx}
      PaperProps={{
        sx: dialogPaperSx,
      }}
      slotProps={{
        backdrop: {
          sx: backdropSx,
        },
      }}
    >
      <Box sx={STATIC_STYLES.closeButtonBox}>
        <CancelRoundedIcon sx={closeIconSx} onClick={handleClose} />
      </Box>

      <DialogTitle sx={dialogTitleSx}>
        <Box sx={iconContainerSx}>
          <StorefrontIcon sx={STATIC_STYLES.storefrontIconSmall} />
        </Box>
        <Typography variant="h4" sx={STATIC_STYLES.titleText}>
          {t('menu.selectDeliveryAddress')}
        </Typography>
        <Typography variant="body2" sx={STATIC_STYLES.subtitleText}>
          Choose your delivery location to continue
        </Typography>
      </DialogTitle>

      <DialogContent sx={STATIC_STYLES.dialogContent}>
        {visibleItems.map((item, index) => (
          <Box
            key={index}
            onClick={() => {
              // For fattyPatty environments - instant close, deferred updates
              if (isFatty) {
                // Check 5-mile delivery radius restriction for existing addresses
                if (item.lat && item.lng && currentStore) {
                  const storeCoords =
                    currentStore._geoloc ||
                    currentStore.coordinates ||
                    currentStore.address;
                  if (storeCoords?.lat && storeCoords?.lng) {
                    const distanceKm = calculateDistance(storeCoords, item);
                    if (distanceKm !== null && distanceKm * 0.621371 > 5) {
                      setDistanceError(
                        'Delivery is not available for this address. It is more than 5 miles from the store.'
                      );
                      return;
                    }
                  }
                }
                setDistanceError('');

                // Close dialog immediately for instant feedback
                handleClose();

                // Defer all state updates to not block UI
                startTransition(() => {
                  // Use the unified hook to set delivery address with source
                  // This respects the priority system (branchMenu > deliveryDialog)
                  setDeliveryAddressById(
                    item.id,
                    item.delivery_store_id || '',
                    'deliveryDialog'
                  );

                  handleFee && handleFee(0);
                  dispatch(LoadDeliveryFees(0));

                  // Notify parent
                  handleDefault(
                    item?.displayName || item?.nickName || item?.id
                  );
                  setErr && setErr('');
                });

                // Fire and forget Firebase update (only if NOT from branchMenu)
                if (!isAddressFromBranchMenu) {
                  setTimeout(() => {
                    firestore
                      .doc(`users/${token}`)
                      .set({ defaultDeliveryAddress: item.id }, { merge: true })
                      .catch((e) => {
                        // Error handled silently
                      });
                  }, 0);
                }

                // Notify parent so it can switch to delivery tab
                if (onAddressChange && typeof onAddressChange === 'function') {
                  onAddressChange(item.id);
                }

                return;
              }

              // For non-FattyPatty, show loading
              (async () => {
                setLoading(index);
                try {
                  const res = await validateUserAddress(token, item, item.id);
                  const fee = await makeAPICall(
                    user?.defaultStore,
                    item?.id,
                    user?.uid
                  );

                  startTransition(() => {
                    handleFee(fee);
                    dispatch(LoadDeliveryFees(fee));
                    const { storeId } = res || {};

                    if (!res.displayName) {
                      handleDefault(res);
                      setErr('');
                    } else {
                      handleDefault(res.displayName);
                      dispatch(
                        setDeliveryStore({
                          address: item.id,
                          store: storeId,
                          source: 'deliveryDialog', // Lower priority than branchMenu
                        })
                      );
                      setAddressStore(storeId);
                    }
                  });

                  handleClose();
                } catch (error) {
                  // Error handled silently
                } finally {
                  setLoading(null);
                }
              })();
            }}
            sx={getAddressBoxSx(user?.defaultDeliveryAddress === item.id)}
          >
            {loading === index ? (
              <Box sx={STATIC_STYLES.loadingBox}>
                <Typography sx={loadingTextSx}>
                  {'Selecting address...'}
                </Typography>
                <Snipper color={environment.mainColor} />
              </Box>
            ) : (
              <Box sx={STATIC_STYLES.addressContentBox}>
                <Box sx={addressIconBoxSx}>
                  <StorefrontIcon sx={addressIconSx} />
                </Box>
                <Box sx={STATIC_STYLES.addressTextBox}>
                  <Typography sx={nickNameTextSx}>
                    {item?.nickName ||
                      (item?.isTemporary ? 'Recent Address' : 'Saved Address')}
                  </Typography>
                  <Typography sx={STATIC_STYLES.displayNameText}>
                    {item?.displayName}
                  </Typography>
                  {item?.isTemporary && (
                    <Typography sx={STATIC_STYLES.temporaryText}>
                      From branch selection
                    </Typography>
                  )}
                </Box>
                {user?.defaultDeliveryAddress === item.id && (
                  <CheckCircleIcon sx={checkIconSx} />
                )}
              </Box>
            )}
          </Box>
        ))}

        {distanceError && (
          <Box
            sx={{
              mb: 2,
              p: '12px 16px',
              backgroundColor: '#FEF2F2',
              borderRadius: '8px',
              border: '1px solid #FECACA',
            }}
          >
            <Typography
              sx={{
                color: '#E53E3E',
                fontSize: '14px',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 500,
              }}
            >
              {distanceError}
            </Typography>
          </Box>
        )}

        <Box sx={STATIC_STYLES.addButtonBox}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClickOpen}
            sx={addButtonSx}
          >
            <AddLocationAltIcon sx={{ fontSize: '20px' }} />
            {t('menu.addNewAddress')}
          </Button>
        </Box>
        <AddressHandlerDialog
          open={openAdd}
          handleClose={handleCloseAdd}
          onAddressAdded={handleAddressAdded}
        />
      </DialogContent>
    </Dialog>
  );
}

export default memo(DeliveryDialog);
